import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFilters, ALL_REVIEW_TITLES_LABEL } from '../../context/FilterContext';
import {
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    TextField,
    Button,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    Breadcrumbs,
    Link,
    tableCellClasses,
    IconButton,
    Tooltip,
    Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MentorReviewTable from '../mentorComponents/mentorReview/MentorReviewTable';
import { getAllSemesters } from '../../services/adminServices/CoeServices';
import {
    getMentorReviewsDashboard,
    getReviewNames,
    getConductedReviewNames,
    getConductedReviewTitles,
    getConductedReviewsReport,
    getConductedReviewLabStats
} from '../../services/mentorServices/ReviewServices';
import { toast } from 'react-toastify';
import '../mentorComponents/mentorReview/MentorReview.css';

const StatInfoIcon = ({ text }) => (
    <Tooltip title={text} arrow placement="top">
        <InfoOutlinedIcon
            sx={{ fontSize: 14, ml: 0.6, color: '#94a3b8', verticalAlign: 'middle', cursor: 'help', '&:hover': { color: '#475569' } }}
        />
    </Tooltip>
);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#7e7e7e',
        color: theme.palette.common.white,
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.5px',
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 13,
        color: '#333',
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fff',
    },
    '&:nth-of-type(even)': {
        backgroundColor: '#fbfbfb',
    },
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.05)',
    },
}));

const highlightMatch = (text, query) => {
    if (!text && text !== 0) return '';
    const textStr = String(text);
    if (!query || !query.trim()) return textStr;

    const words = query
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (words.length === 0) return textStr;

    const sortedWords = [...new Set(words)].sort((a, b) => b.length - a.length);
    const regex = new RegExp(`(${sortedWords.join('|')})`, 'gi');
    const parts = textStr.split(regex);

    if (parts.length <= 1) return textStr;

    const lowerWords = new Set(sortedWords.map(w => w.toLowerCase()));

    return parts.map((part, index) =>
        lowerWords.has(part.toLowerCase()) ? (
            <Box
                component="mark"
                key={index}
                sx={{
                    backgroundColor: '#ffe082',
                    color: '#212529',
                    fontWeight: 'inherit',
                    borderRadius: '2px',
                    px: '2px',
                    py: 0
                }}
            >
                {part}
            </Box>
        ) : (
            part
        )
    );
};

const reviewDateFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const parseDateFromReview = (dateVal) => {
    if (dateVal) {
        const parsed = new Date(dateVal);
        if (!isNaN(parsed.getTime())) {
            return reviewDateFormatter.format(parsed);
        }
    }
    return '-';
};

const extractErrorMessage = (err, fallback = "An error occurred") => {
    if (!err) return fallback;
    const resData = err?.response?.data;
    if (resData) {
        const details = resData?.error?.details || resData?.details;
        if (Array.isArray(details) && details.length > 0) {
            const detailMessages = details
                .map(d => (typeof d === 'string' ? d : d?.message || d?.msg || (d?.field ? `${d.field}: invalid` : '')))
                .filter(Boolean);
            if (detailMessages.length > 0) {
                return detailMessages.join(', ');
            }
        }
        if (typeof resData?.message === 'string' && resData.message.trim()) {
            return resData.message;
        }
        if (typeof resData?.error?.message === 'string' && resData.error.message.trim()) {
            return resData.error.message;
        }
        if (typeof resData?.error === 'string' && resData.error.trim()) {
            return resData.error;
        }
        if (typeof resData === 'string' && resData.trim() && !resData.trim().startsWith('<')) {
            return resData;
        }
    }
    if (typeof err?.message === 'string' && err.message.trim()) {
        return err.message;
    }
    return fallback;
};

const ReportViewerReview = () => {
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const userCoeId = user?.coeId || user?.coe_id || user?.coeCenter?.id || user?.coe_center_id || user?.coe || '';

    const { reportViewerReviewState, setReportViewerReviewState } = useFilters();

    const [labStatsFetchedFor, setLabStatsFetchedFor] = useState(null);
    const [semester, setSemester] = useState(reportViewerReviewState.semester || '');
    const [reviewNameFilter, setReviewNameFilter] = useState(reportViewerReviewState.reviewName || 'all');
    const [reviewNameInput, setReviewNameInput] = useState(reportViewerReviewState.reviewNameInput || ALL_REVIEW_TITLES_LABEL);
    const [apiReviewNames, setApiReviewNames] = useState(reportViewerReviewState.options?.apiReviewNames || []);
    const [apiReviewTitles, setApiReviewTitles] = useState(reportViewerReviewState.options?.apiReviewTitles || []);
    const [semesters, setSemesters] = useState(reportViewerReviewState.options?.semesters || []);
    const [sortBy, setSortBy] = useState(reportViewerReviewState.sortBy || 'latest');
    const [initialLoading, setInitialLoading] = useState(!reportViewerReviewState.hasFetched);
    const [actionLoading, setActionLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(
        reportViewerReviewState.lastUpdated ? new Date(reportViewerReviewState.lastUpdated) : new Date()
    );
    const [currentPage, setCurrentPage] = useState(reportViewerReviewState.currentPage || 1);
    const [rowsPerPage, setRowsPerPage] = useState(reportViewerReviewState.rowsPerPage || 10);
    const [cardsPage, setCardsPage] = useState(reportViewerReviewState.cardsPage || 1);
    const [cardsPerPage, setCardsPerPage] = useState(reportViewerReviewState.cardsPerPage || 10);
    const [showTable, setShowTable] = useState(reportViewerReviewState.showTable !== undefined ? reportViewerReviewState.showTable : true);
    const [reviewsData, setReviewsData] = useState(reportViewerReviewState.reviewsData || []);
    const [stats, setStats] = useState(reportViewerReviewState.stats || {
        totalLearners: 0,
        totalLearnersGivenReview: undefined,
        totalConductedReviews: undefined,
        totalReviews: 0,
        averageReviews: 0,
        averageScore: 0,
        maxScore: 0,
        maxReviews: 0,
    });

    // Drill-down states
    // 'reviews': individual review cards (Review 1, Review 2...)
    // 'lab_wise': lab-wise table for selected review
    // 'learner_wise': detailed learner table for selected review & lab
    const [viewMode, setViewMode] = useState(reportViewerReviewState.viewMode || 'reviews');
    const [selectedReview, setSelectedReview] = useState(reportViewerReviewState.selectedReview || null);
    const [selectedReviewId, setSelectedReviewId] = useState(reportViewerReviewState.selectedReviewId || null);
    const [selectedLab, setSelectedLab] = useState(reportViewerReviewState.selectedLab || null);
    const [selectedLabId, setSelectedLabId] = useState(reportViewerReviewState.selectedLabId || null);
    const [learnerSubmissions, setLearnerSubmissions] = useState(reportViewerReviewState.learnerSubmissions || []);
    const [learnerLoading, setLearnerLoading] = useState(false);
    const [reviewSearchText, setReviewSearchText] = useState(reportViewerReviewState.reviewSearchText || '');
    const [labSearchText, setLabSearchText] = useState(reportViewerReviewState.labSearchText || '');
    const [apiLabWiseData, setApiLabWiseData] = useState(reportViewerReviewState.apiLabWiseData || []);
    const [labStatsLoading, setLabStatsLoading] = useState(false);

    // Sync state with FilterContext
    useEffect(() => {
        setReportViewerReviewState(prev => ({
            ...prev,
            semester,
            reviewName: reviewNameFilter,
            reviewNameInput,
            sortBy,
            currentPage,
            rowsPerPage,
            cardsPage,
            cardsPerPage,
            showTable,
            viewMode,
            selectedReview,
            selectedReviewId,
            selectedLab,
            selectedLabId,
            learnerSubmissions,
            apiLabWiseData,
            reviewSearchText,
            labSearchText,
            reviewsData,
            totalRecords: reviewsData.length,
            stats,
            lastUpdated,
            options: { semesters, apiReviewNames, apiReviewTitles },
            hasFetched: prev.hasFetched
        }));
    }, [
        semester, reviewNameFilter, reviewNameInput, sortBy,
        currentPage, rowsPerPage, cardsPage, cardsPerPage, showTable, viewMode,
        selectedReview, selectedReviewId, selectedLab, selectedLabId,
        learnerSubmissions, apiLabWiseData, reviewSearchText, labSearchText,
        reviewsData, stats, lastUpdated, semesters, apiReviewNames, apiReviewTitles,
        setReportViewerReviewState
    ]);

    // Helper to get reviewId from reviewName (or from review object)
    const getReviewIdByName = useCallback((name) => {
        if (!name || name === 'all') return '';
        const lowerName = String(name).trim().toLowerCase();

        // 1. Check apiReviewNames (from /conducted-review-names)
        for (const item of apiReviewNames) {
            if (typeof item === 'object' && item) {
                const rName = String(item.reviewName || item.review_name || item.name || '').trim().toLowerCase();
                if (rName === lowerName && (item.reviewId || item.review_id || item.id)) {
                    return item.reviewId || item.review_id || item.id;
                }
            }
        }

        // 2. Check reviewsData (from /conducted-reviews report)
        for (const item of reviewsData) {
            const rName = String(item?.reviewName || item?.reviewTitle || item?.review_name || item?.review?.name || item?.name || '').trim().toLowerCase();
            if (rName === lowerName && (item?.reviewId || item?.review_id || item?.review?.id || item?.id)) {
                return item.reviewId || item.review_id || item.review?.id || item.id;
            }
        }

        return '';
    }, [apiReviewNames, reviewsData]);

    // Helper to get reviewName from reviewId
    const getReviewNameById = useCallback((id) => {
        if (!id || id === 'all') return '';
        const idStr = String(id).trim();

        // 1. Check apiReviewNames
        for (const item of apiReviewNames) {
            if (typeof item === 'object' && item) {
                const rId = String(item.reviewId || item.review_id || item.id || '').trim();
                if (rId === idStr && (item.reviewName || item.review_name || item.name)) {
                    return item.reviewName || item.review_name || item.name;
                }
            }
        }

        // 2. Check reviewsData
        for (const item of reviewsData) {
            const rId = String(item?.reviewId || item?.review_id || item?.review?.id || item?.id || '').trim();
            if (rId === idStr && (item?.reviewName || item?.reviewTitle || item?.review_name || item?.name)) {
                return item.reviewName || item.reviewTitle || item.review_name || item.name;
            }
        }

        return '';
    }, [apiReviewNames, reviewsData]);

    // Fetch conducted review names for dropdown
    const fetchReviewNamesList = useCallback(async () => {
        try {
            const response = await getConductedReviewNames();
            const resData = response?.data?.data || response?.data;
            let rawList = [];
            if (Array.isArray(resData?.reviewNames)) {
                rawList = resData.reviewNames;
            } else if (Array.isArray(resData)) {
                rawList = resData;
            } else if (Array.isArray(resData?.names)) {
                rawList = resData.names;
            } else if (Array.isArray(response?.data?.reviewNames)) {
                rawList = response.data.reviewNames;
            }
            const cleanItems = rawList.map(item => {
                if (typeof item === 'string') {
                    return { reviewId: item, reviewName: item };
                }
                const rName = item?.reviewName || item?.review_name || item?.name || item?.reviewTitle || '';
                const rId = item?.reviewId || item?.review_id || item?.id || rName;
                return { reviewId: rId, reviewName: rName };
            }).filter(item => Boolean(item.reviewName));

            setApiReviewNames(cleanItems);
            setReportViewerReviewState(prev => ({
                ...prev,
                options: { ...prev.options, apiReviewNames: cleanItems }
            }));
            return true;
        } catch (err) {
            console.error("Failed to fetch conducted review names:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch conducted review names"));
            setApiReviewNames([]);
            return false;
        }
    }, [setReportViewerReviewState]);

    // Fetch conducted review titles for dropdown
    const fetchReviewTitlesList = useCallback(async (semesterOverride) => {
        if (!userCoeId) return null;
        try {
            const response = await getConductedReviewTitles({
                coe_id: userCoeId,
                semester: semesterOverride !== undefined ? semesterOverride : semester
            });
            const resData = response?.data?.data || response?.data;
            const rawList = Array.isArray(resData?.reviewTitles) ? resData.reviewTitles : (Array.isArray(resData) ? resData : []);
            const cleanItems = rawList.map(item => ({
                reviewTitle: typeof item === 'string' ? item : (item?.reviewTitle || item?.review_title || ''),
                reviewIds: Array.isArray(item?.reviewIds) ? item.reviewIds : [],
                reviewNames: Array.isArray(item?.reviewNames) ? item.reviewNames : [],
                totalReviews: item?.totalReviews ?? 0
            })).filter(item => Boolean(item.reviewTitle));

            setApiReviewTitles(cleanItems);
            setReportViewerReviewState(prev => ({
                ...prev,
                options: { ...prev.options, apiReviewTitles: cleanItems }
            }));
            return cleanItems;
        } catch (err) {
            console.error("Failed to fetch conducted review titles:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch conducted review titles"));
            setApiReviewTitles([]);
            return null;
        }
    }, [userCoeId, semester, setReportViewerReviewState]);

    // Fetch semesters for user's COE
    const fetchSemesters = useCallback(async (coeId) => {
        if (!coeId) {
            setSemesters([]);
            return false;
        }
        try {
            const response = await getAllSemesters(coeId);
            const sortedSemesters = response?.data?.data?.sort((a, b) => a - b) || [];
            setSemesters(sortedSemesters);
            setReportViewerReviewState(prev => ({
                ...prev,
                options: { ...prev.options, semesters: sortedSemesters }
            }));
            return true;
        } catch (err) {
            console.error("Failed to fetch semesters:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch semesters"));
            setSemesters([]);
            return false;
        }
    }, [setReportViewerReviewState]);

    // Main fetch data function using conducted reviews report API
    const fetchData = useCallback(async (customPage = 1, customLimit = 50000, filterOverrides = {}, isInitial = false, isAction = false) => {
        if (!userCoeId) {
            toast.error("COE ID not found. Please log in again.");
            setInitialLoading(false);
            setActionLoading(false);
            return;
        }

        if (isInitial) {
            setInitialLoading(true);
        }
        if (isAction) {
            setActionLoading(true);
        }
        try {
            const currentSemester = 'semester' in filterOverrides ? filterOverrides.semester : semester;
            const currentReviewName = 'reviewName' in filterOverrides ? filterOverrides.reviewName : reviewNameFilter;
            const currentSort = 'sort' in filterOverrides ? filterOverrides.sort : sortBy;

            const apiParams = {
                page: customPage,
                limit: customLimit,
                coe_id: userCoeId,
                semester: (currentSemester && currentSemester !== 'all') ? currentSemester : undefined,
                enrolment_year: 2025,
                passout_year: 2029,
                review_title: (currentReviewName && currentReviewName !== 'all') ? currentReviewName : undefined,
                sort: currentSort,
            };

            const response = await getConductedReviewsReport(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];

            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.conductedReviews)
                    ? resData.conductedReviews
                    : (Array.isArray(resData.reviews)
                        ? resData.reviews
                        : (Array.isArray(resData.data)
                            ? resData.data
                            : (Array.isArray(resData.list) ? resData.list : [])));
            }

            setReviewsData(list);
            setReportViewerReviewState(prev => ({ ...prev, hasFetched: true }));

            // Calculate Top 6 Summary Stats
            const rawSummary = resData?.summary || resData?.stats || (resData && typeof resData === 'object' && !Array.isArray(resData) ? resData : {});

            const getVal = (...keys) => {
                for (const k of keys) {
                    if (rawSummary && rawSummary[k] !== undefined && rawSummary[k] !== null && rawSummary[k] !== '') {
                        return rawSummary[k];
                    }
                }
                return undefined;
            };

            let totalLearners = getVal('totalLearners', 'total_learners');
            let totalLearnersGivenReview = getVal('totalLearnersGivenReview', 'total_learners_given_review', 'learnersGivenReview', 'learners_given_review');
            let totalConductedReviews = getVal('totalConductedReviews', 'total_conducted_reviews');
            let totalReviews = totalConductedReviews !== undefined && totalConductedReviews !== null && totalConductedReviews !== ''
                ? totalConductedReviews
                : getVal('totalReviews', 'total_reviews');
            let averageReviews = getVal('averageReviews', 'average_reviews', 'avgReviews', 'avg_reviews');
            let averageScore = getVal('averageScore', 'average_score', 'avgScore', 'avg_score');
            let maxScore = getVal('maxScore', 'max_score');
            let maxReviews = getVal('maxReviews', 'max_reviews');

            if (totalReviews === undefined) {
                totalReviews = resData?.pagination?.totalItems || resData?.totalRecords || resData?.totalCount || list.length;
            }

            if (totalLearners === undefined) {
                const uniqueLearners = new Set(
                    list.map(item => item?.learnerId || item?.learner_id || item?.learnerCode || item?.learner_code || item?.learnerName || item?.learner_name || item?.Learner?.name || item?.name).filter(Boolean)
                );
                totalLearners = uniqueLearners.size;
            }

            if (totalLearnersGivenReview === undefined && list.length > 0) {
                const uniqueGiven = new Set(
                    list.map(item => item?.learnerId || item?.learner_id || item?.learnerCode || item?.learner_code || item?.learnerName || item?.learner_name || item?.Learner?.name || item?.name).filter(Boolean)
                );
                if (uniqueGiven.size > 0 && Number(totalLearners) > 0) {
                    totalLearnersGivenReview = uniqueGiven.size;
                }
            }

            const validScores = list
                .map(item => {
                    const s = item?.score ?? item?.finalScore ?? item?.reviewScore ?? item?.totalScore;
                    return s !== undefined && s !== null && s !== '' ? Number(s) : NaN;
                })
                .filter(s => !isNaN(s));

            if (averageScore === undefined) {
                averageScore = validScores.length > 0
                    ? Number((validScores.reduce((acc, curr) => acc + curr, 0) / validScores.length).toFixed(1))
                    : 0;
            }

            if (maxScore === undefined) {
                maxScore = validScores.length > 0 ? Math.max(...validScores) : 0;
            }

            const reviewsPerLearnerMap = {};
            list.forEach(item => {
                const lid = item?.learnerId || item?.learner_id || item?.learnerCode || item?.learner_code || item?.learnerName || item?.name || 'unknown';
                reviewsPerLearnerMap[lid] = (reviewsPerLearnerMap[lid] || 0) + 1;
            });
            const reviewCounts = Object.values(reviewsPerLearnerMap);

            if (averageReviews === undefined) {
                if (Number(totalLearners) > 0 && Number(totalReviews) > 0) {
                    averageReviews = Number((Number(totalReviews) / Number(totalLearners)).toFixed(1));
                } else {
                    averageReviews = 0;
                }
            }

            if (maxReviews === undefined) {
                maxReviews = reviewCounts.length > 0 ? Math.max(...reviewCounts) : (totalReviews > 0 ? 1 : 0);
            }

            const calculatedStats = {
                totalLearners: Number(totalLearners) || 0,
                totalLearnersGivenReview: totalLearnersGivenReview !== undefined && totalLearnersGivenReview !== null && totalLearnersGivenReview !== ''
                    ? Number(totalLearnersGivenReview)
                    : undefined,
                totalConductedReviews: totalConductedReviews !== undefined && totalConductedReviews !== null && totalConductedReviews !== ''
                    ? Number(totalConductedReviews)
                    : undefined,
                totalReviews: Number(totalReviews) || 0,
                averageReviews: Number(averageReviews) || 0,
                averageScore: Number(averageScore) || 0,
                maxScore: Number(maxScore) || 0,
                maxReviews: Number(maxReviews) || 0,
            };
            setStats(calculatedStats);
            const now = new Date();
            setLastUpdated(now);
            setReportViewerReviewState(prev => ({
                ...prev,
                reviewsData: list,
                totalRecords: list.length,
                stats: calculatedStats,
                lastUpdated: now,
                hasFetched: true
            }));
            return true;
        } catch (err) {
            console.error("Failed to fetch reviews:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch reviews"));
            setReviewsData([]);
            return false;
        } finally {
            setInitialLoading(false);
            setActionLoading(false);
        }
    }, [userCoeId, semester, reviewNameFilter, sortBy, setReportViewerReviewState]);

    // Fetch lab-wise stats for selected review (only this API requires reviewId instead of reviewName)
    const fetchLabStats = useCallback(async (rId, rName, semesterOverride) => {
        let targetReviewId = rId;
        if (!targetReviewId || targetReviewId === rName) {
            targetReviewId = getReviewIdByName(rName || selectedReview) || rId;
        }
        if (!targetReviewId || !userCoeId) return false;
        setLabStatsLoading(true);
        try {
            const response = await getConductedReviewLabStats(targetReviewId, {
                coe_id: userCoeId,
                semester: (semesterOverride !== undefined ? semesterOverride : semester) || undefined,
            });
            const resData = response?.data?.data || response?.data || [];
            const labList = Array.isArray(resData) ? resData : (resData?.labs || resData?.list || resData?.data || []);
            const safeLabList = Array.isArray(labList) ? labList : [];
            setApiLabWiseData(safeLabList);
            setLabStatsFetchedFor(String(targetReviewId));
            setReportViewerReviewState(prev => ({ ...prev, apiLabWiseData: safeLabList }));
            return true;
        } catch (err) {
            console.error("Failed to fetch lab stats:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch lab stats"));
            setApiLabWiseData([]);
            return false;
        } finally {
            setLabStatsLoading(false);
        }
    }, [userCoeId, semester, setReportViewerReviewState, getReviewIdByName, selectedReview]);

    // Fetch learner review submissions for a specific lab and review (must pass reviewName)
    const fetchLearnerDataForLab = useCallback(async (targetLabId, targetLabName, targetReviewName, semesterOverride) => {
        if (!userCoeId) return false;
        setLearnerLoading(true);
        try {
            // In learner submission, pass review name (not reviewId)
            let rName = targetReviewName && targetReviewName !== 'all' ? targetReviewName : undefined;
            if (rName && /^[0-9a-fA-F]{24}$/.test(rName)) {
                rName = getReviewNameById(rName) || rName;
            }
            const apiParams = {
                coe_id: userCoeId,
                reviewName: rName,
                lab_id: targetLabId && targetLabId !== 'all' ? targetLabId : undefined,
                semester: (semesterOverride !== undefined ? semesterOverride : semester) || undefined,
                page: 1,
                limit: 50000,
            };
            const response = await getMentorReviewsDashboard(apiParams);
            const resData = response?.data?.data || response?.data;
            let list = [];
            if (Array.isArray(resData)) {
                list = resData;
            } else if (resData && typeof resData === 'object') {
                list = Array.isArray(resData.reviews) ? resData.reviews : (Array.isArray(resData.data) ? resData.data : (Array.isArray(resData.list) ? resData.list : []));
            }
            const safeSubmissions = (Array.isArray(list) ? list : []).filter(item => {
                const s = String(item?.status || item?.reviewStatus || item?.review_status || '').trim().toLowerCase();
                return s === 'completed' || s === 'tlc_published';
            });
            setLearnerSubmissions(safeSubmissions);
            setReportViewerReviewState(prev => ({ ...prev, learnerSubmissions: safeSubmissions }));
            return true;
        } catch (err) {
            console.error("Failed to fetch learner reviews for lab:", err);
            toast.error(extractErrorMessage(err, "Failed to fetch learner reviews for lab"));
            setLearnerSubmissions([]);
            return false;
        } finally {
            setLearnerLoading(false);
        }
    }, [userCoeId, semester, setReportViewerReviewState]);

    // Initial load: fetch options and data ONLY IF not already fetched in context
    useEffect(() => {
        if (apiReviewNames.length === 0) {
            fetchReviewNamesList();
        }
        if (userCoeId && apiReviewTitles.length === 0) {
            fetchReviewTitlesList();
        }
        if (userCoeId && semesters.length === 0) {
            fetchSemesters(userCoeId);
        }
        if (userCoeId && !reportViewerReviewState.hasFetched) {
            fetchData(1, 50000, {}, true, false);
        } else {
            setInitialLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCoeId]);

    // Auto-refresh dashboard every 1 minute (60 seconds)
    useEffect(() => {
        if (!userCoeId) return;

        const intervalId = setInterval(async () => {
            try {
                setIsRefreshing(true);
                let success = false;
                if (viewMode === 'learner_wise') {
                    // Learner submission: pass reviewName
                    success = await fetchLearnerDataForLab(selectedLabId || selectedLab, selectedLab, selectedReview);
                } else if (viewMode === 'lab_wise' && (selectedReviewId || selectedReview)) {
                    // Labwise: pass reviewId
                    const rId = selectedReviewId || getReviewIdByName(selectedReview);
                    success = await fetchLabStats(rId, selectedReview);
                } else {
                    // Review breakdown: pass reviewName
                    success = await fetchData(1, 50000, {}, false, false);
                }
                if (success) {
                    setLastUpdated(new Date());
                }
            } finally {
                setIsRefreshing(false);
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [fetchData, userCoeId, viewMode, selectedLabId, selectedLab, selectedReview, selectedReviewId, fetchLabStats, fetchLearnerDataForLab, getReviewIdByName]);

    // Manual refresh handler
    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        try {
            let success = false;
            if (viewMode === 'learner_wise') {
                // Learner submission: pass reviewName
                success = await fetchLearnerDataForLab(selectedLabId || selectedLab, selectedLab, selectedReview);
            } else if (viewMode === 'lab_wise' && (selectedReviewId || selectedReview)) {
                // Labwise: pass reviewId
                const rId = selectedReviewId || getReviewIdByName(selectedReview);
                success = await fetchLabStats(rId, selectedReview);
            } else {
                // Review breakdown: pass reviewName
                success = await fetchData(1, 50000, { reviewName: reviewNameFilter !== 'all' ? reviewNameFilter : 'all' }, false, false);
            }
            if (success) {
                setLastUpdated(new Date());
                toast.success("Dashboard refreshed", { autoClose: 1500 });
            }
        } catch (err) {
            toast.error(extractErrorMessage(err, "Failed to refresh dashboard"));
        } finally {
            setIsRefreshing(false);
        }
    };

    // Handle Semester change
    const handleSemesterChange = (event) => {
        const val = event.target.value;
        setSemester(val);
        setCurrentPage(1);
        const titlesPromise = fetchReviewTitlesList(val);
        if (viewMode === 'lab_wise' && (selectedReviewId || selectedReview)) {
            fetchLabStats(selectedReviewId || getReviewIdByName(selectedReview), selectedReview, val);
            return;
        }
        if (viewMode === 'learner_wise') {
            fetchLearnerDataForLab(selectedLabId || selectedLab, selectedLab, selectedReview, val);
            return;
        }
        // Drop the selected title if it has no reviews in the new semester
        titlesPromise.then(titles => {
            const stale = reviewNameFilter !== 'all' && titles && !titles.some(t => t.reviewTitle === reviewNameFilter);
            if (stale) {
                setReviewNameFilter('all');
                setReviewNameInput(ALL_REVIEW_TITLES_LABEL);
            }
            fetchData(1, 50000, stale ? { semester: val, reviewName: 'all' } : { semester: val }, false, true);
        });
    };

    // Review title options for Autocomplete dropdown
    const reviewNameOptions = useMemo(() => {
        const titles = apiReviewTitles
            .map(t => t.reviewTitle)
            .sort((x, y) => x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' }));

        return [
            { label: ALL_REVIEW_TITLES_LABEL, value: 'all', reviewName: 'all', reviewId: 'all' },
            ...titles.map(title => ({ label: title, value: title, reviewName: title, reviewTitle: title }))
        ];
    }, [apiReviewTitles]);

    // Review names that belong to the selected title (used for client-side filtering)
    const selectedTitleReviewNames = useMemo(() => {
        if (reviewNameFilter === 'all') return null;
        const match = apiReviewTitles.find(t => t.reviewTitle === reviewNameFilter);
        return match ? new Set(match.reviewNames.map(n => String(n).toLowerCase())) : null;
    }, [apiReviewTitles, reviewNameFilter]);

    const matchesSelectedTitle = useCallback((rev) => {
        if (reviewNameFilter === 'all') return true;
        const title = String(reviewNameFilter).toLowerCase();
        if (String(rev.reviewTitle || '').toLowerCase() === title) return true;
        return Boolean(selectedTitleReviewNames && selectedTitleReviewNames.has(String(rev.reviewName).toLowerCase()));
    }, [reviewNameFilter, selectedTitleReviewNames]);

    const formatStatValue = (val) => {
        if (val === undefined || val === null || val === '') return '00';
        const num = Number(val);
        if (isNaN(num)) return val;
        if (Number.isInteger(num)) {
            return num >= 0 && num < 10 ? `0${num}` : num;
        }
        return parseFloat(num.toFixed(2));
    };

    // Group reviews into individual review cards
    const reviewsSummaryList = useMemo(() => {
        if (!reviewsData || reviewsData.length === 0) return [];

        // Check if items are already pre-aggregated conductedReviews objects from backend
        const isPreAggregated = reviewsData.some(item =>
            item && (item.totalReviewsTaken !== undefined || (item.reviewId && item.avgScore !== undefined && !item.learnerId))
        );

        if (isPreAggregated) {
            return reviewsData.map(item => {
                const rName = item?.reviewName || item?.review_name || item?.reviewTitle || item?.name || 'General Review';
                const rId = item?.reviewId || item?.review_id || item?.id || rName;
                const totalLearners = item?.totalLearners ?? item?.total_learners ?? 0;
                const totalLearnersGivenReview = item?.totalLearnersGivenReview ?? item?.total_learners_given_review ?? item?.learnersGivenReview ?? undefined;
                const totalReviews = item?.totalConductedReviews ?? item?.totalReviewsTaken ?? item?.totalReviews ?? item?.total_reviews ?? 0;
                const latestDate = parseDateFromReview(item?.reviewDate ?? item?.review_date);
                const avgScore = item?.avgScore ?? item?.avg_score ?? 0;
                const maxScore = item?.maxScore ?? item?.max_score ?? 0;
                const completed = item?.completed ?? item?.completedCount ?? 0;
                const inProgress = item?.inProgress ?? item?.inProgressCount ?? 0;

                return {
                    reviewId: rId,
                    reviewName: rName,
                    reviewTitle: item?.reviewTitle || item?.review_title || '',
                    totalLearners: Number(totalLearners) || 0,
                    totalLearnersGivenReview: totalLearnersGivenReview !== undefined ? Number(totalLearnersGivenReview) : undefined,
                    totalReviews: Number(totalReviews) || 0,
                    reviewDate: item?.reviewDate ?? item?.review_date ?? null,
                    latestDate,
                    avgScore: Number(avgScore) || 0,
                    maxScore: Number(maxScore) || 0,
                    completed: Number(completed) || 0,
                    inProgress: Number(inProgress) || 0,
                    raw: item
                };
            }).filter(matchesSelectedTitle);
        }

        const groups = {};
        reviewsData.forEach(item => {
            const rName = item?.reviewName || item?.review_name || item?.reviewTitle || item?.name || item?.review?.name || item?.review?.reviewName || 'General Review';
            const rId = item?.reviewId || item?.review_id || item?.review?.id || item?.id;

            if (!groups[rName]) {
                groups[rName] = {
                    reviewName: rName,
                    reviewId: rId,
                    items: [],
                    uniqueLearners: new Set(),
                    dates: [],
                    scores: [],
                    completedCount: 0,
                    inProgressCount: 0,
                };
            }
            if (rId && !groups[rName].reviewId) {
                groups[rName].reviewId = rId;
            }
            groups[rName].items.push(item);

            const learnerKey = item?.learnerId || item?.learner_id || item?.learnerCode || item?.learnerName || item?.rollNo;
            if (learnerKey) groups[rName].uniqueLearners.add(learnerKey);

            const d = item?.submissionDate || item?.submission_date || item?.created_at || item?.submittedDate;
            if (d) {
                const parsedDate = new Date(d);
                if (!isNaN(parsedDate.getTime())) {
                    groups[rName].dates.push(parsedDate);
                }
            }

            const isCompleted = ['completed', 'tlc_published'].includes((item?.status || '').toLowerCase());
            if (isCompleted) {
                groups[rName].completedCount += 1;
            } else {
                groups[rName].inProgressCount += 1;
            }

            const score = item?.score ?? item?.finalScore ?? item?.reviewScore ?? item?.totalScore;
            if (score !== undefined && score !== null && score !== '') {
                const numScore = Number(score);
                if (!isNaN(numScore)) {
                    groups[rName].scores.push(numScore);
                }
            }
        });

        return Object.values(groups).map(g => {
            const totalReviews = g.items.length;
            const totalLearners = g.uniqueLearners.size;

            let latestDateStr = '-';
            if (g.dates.length > 0) {
                const maxDate = new Date(Math.max(...g.dates));
                latestDateStr = reviewDateFormatter.format(maxDate);
            }

            const avgScore = g.scores.length > 0
                ? Number((g.scores.reduce((a, b) => a + b, 0) / g.scores.length).toFixed(2))
                : 0;
            const maxScore = g.scores.length > 0 ? Math.max(...g.scores) : 0;

            return {
                reviewName: g.reviewName,
                reviewId: g.reviewId || g.reviewName,
                totalLearners,
                totalReviews,
                latestDate: latestDateStr,
                avgScore,
                maxScore,
                completed: g.completedCount,
                inProgress: g.inProgressCount,
                items: g.items
            };
        }).filter(matchesSelectedTitle).sort((a, b) => {
            return b.totalReviews - a.totalReviews;
        });
    }, [reviewsData, matchesSelectedTitle]);

    // Filtered reviews summary list based on search text in Review Breakdown
    const filteredReviewsSummaryList = useMemo(() => {
        const trimmed = reviewSearchText.trim();
        if (!trimmed) return reviewsSummaryList;
        const words = trimmed.toLowerCase().split(/\s+/).filter(Boolean);

        return reviewsSummaryList.filter(rev => {
            const name = (rev.reviewName || '').toLowerCase();
            const title = (rev.reviewTitle || '').toLowerCase();
            const date = (rev.latestDate || '').toLowerCase();
            return words.every(word => name.includes(word) || title.includes(word) || date.includes(word));
        });
    }, [reviewsSummaryList, reviewSearchText]);

    // Pagination for Review Cards
    useEffect(() => {
        setCardsPage(1);
    }, [reviewSearchText, reviewNameFilter, semester]);

    const totalCardPages = Math.ceil(filteredReviewsSummaryList.length / cardsPerPage);

    // Safeguard cardsPage if it exceeds totalCardPages when cardsPerPage is increased
    useEffect(() => {
        if (totalCardPages > 0 && cardsPage > totalCardPages) {
            setCardsPage(1);
        }
    }, [cardsPage, totalCardPages]);

    const paginatedReviewCards = useMemo(() => {
        const start = (cardsPage - 1) * cardsPerPage;
        return filteredReviewsSummaryList.slice(start, start + cardsPerPage);
    }, [filteredReviewsSummaryList, cardsPage, cardsPerPage]);

    // Group lab-wise data for the selected review
    const labWiseData = useMemo(() => {
        if (!selectedReview) return [];

        // If backend API provided lab stats, use them
        if (apiLabWiseData && apiLabWiseData.length > 0) {
            const list = apiLabWiseData.map(item => {
                const labName = item?.labName || item?.lab_name || item?.name || item?.labId || item?.lab_id || 'General Lab';
                const labId = item?.labId || item?.lab_id || item?.id || labName;
                const totalLearners = item?.totalLearners ?? item?.total_learners ?? item?.learnersCount ?? item?.learners_count ?? item?.uniqueLearners ?? 0;
                const totalReviews = item?.totalReviews ?? item?.total_reviews ?? item?.reviewsCount ?? item?.reviews_count ?? 0;
                const avgScore = item?.avgScore ?? item?.avg_score ?? item?.averageScore ?? item?.average_score ?? 0;
                const maxScore = item?.maxScore ?? item?.max_score ?? 0;
                const completed = item?.completed ?? item?.completedCount ?? item?.completed_count ?? 0;
                const inProgress = item?.inProgress ?? item?.in_progress ?? item?.inProgressCount ?? item?.in_progress_count ?? 0;

                return {
                    labId,
                    labName,
                    totalLearners: Number(totalLearners) || 0,
                    totalReviews: Number(totalReviews) || 0,
                    avgScore: Number(avgScore) || 0,
                    maxScore: Number(maxScore) || 0,
                    completed: Number(completed) || 0,
                    inProgress: Number(inProgress) || 0,
                    items: item?.items || []
                };
            });

            if (labSearchText.trim()) {
                const q = labSearchText.trim().toLowerCase();
                return list.filter(l => (l.labName || '').toLowerCase().includes(q) || String(l.labId).toLowerCase().includes(q));
            }

            return list.sort((a, b) => (a.labName || '').localeCompare(b.labName || ''));
        }

        // The lab-stats API answered with no labs for this review/semester: show no data instead of aggregating a fallback
        if (labStatsFetchedFor && selectedReviewId && labStatsFetchedFor === String(selectedReviewId)) return [];

        // Fallback: aggregate from reviewsData
        const reviewItems = reviewsData.filter(item => {
            const rName = item?.reviewName || item?.review_name || item?.reviewTitle || item?.name;
            return (rName || '').toLowerCase() === selectedReview.toLowerCase();
        });

        const labGroups = {};
        reviewItems.forEach(item => {
            const labId = item?.labName || item?.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.labId || item?.lab_id || 'General Lab';
            if (!labGroups[labId]) {
                labGroups[labId] = {
                    labId,
                    labName: labId,
                    uniqueLearners: new Set(),
                    items: [],
                    scores: [],
                    completedCount: 0,
                    inProgressCount: 0
                };
            }
            labGroups[labId].items.push(item);

            const lKey = item?.learnerId || item?.learner_id || item?.learnerCode || item?.learnerName || item?.rollNo;
            if (lKey) labGroups[labId].uniqueLearners.add(lKey);

            const isCompleted = ['completed', 'tlc_published'].includes((item?.status || '').toLowerCase());
            if (isCompleted) {
                labGroups[labId].completedCount += 1;
            } else {
                labGroups[labId].inProgressCount += 1;
            }

            const score = item?.score ?? item?.finalScore ?? item?.reviewScore ?? item?.totalScore;
            if (score !== undefined && score !== null && score !== '') {
                const numScore = Number(score);
                if (!isNaN(numScore)) {
                    labGroups[labId].scores.push(numScore);
                }
            }
        });

        const list = Object.values(labGroups).map(g => {
            const avgScore = g.scores.length > 0
                ? Number((g.scores.reduce((a, b) => a + b, 0) / g.scores.length).toFixed(2))
                : 0;
            const maxScore = g.scores.length > 0 ? Math.max(...g.scores) : 0;
            return {
                labId: g.labId,
                labName: g.labName,
                totalLearners: g.uniqueLearners.size,
                totalReviews: g.items.length,
                avgScore,
                maxScore,
                completed: g.completedCount,
                inProgress: g.inProgressCount,
                items: g.items
            };
        });

        if (labSearchText.trim()) {
            const q = labSearchText.trim().toLowerCase();
            return list.filter(l => String(l.labName || l.labId || '').toLowerCase().includes(q));
        }

        return list.sort((a, b) => String(a.labName || a.labId || '').localeCompare(String(b.labName || b.labId || '')));
    }, [selectedReview, selectedReviewId, labStatsFetchedFor, apiLabWiseData, reviewsData, labSearchText]);

    // Learner data for detailed table (filtered by selectedReview and selectedLab)
    // Learner data for detailed table (filtered by selectedReview, selectedLab, and ONLY completed status)
    const learnerWiseData = useMemo(() => {
        let items = [];
        if (learnerSubmissions && learnerSubmissions.length > 0) {
            items = learnerSubmissions;
        } else if (!selectedLab) {
            return [];
        } else {
            const selectedLabStr = String(selectedLab).toLowerCase();
            const selectedLabIdStr = selectedLabId !== null && selectedLabId !== undefined ? String(selectedLabId).toLowerCase() : '';
            const selectedReviewStr = selectedReview ? String(selectedReview).toLowerCase() : '';

            // Check if apiLabWiseData has items for the selected lab
            if (apiLabWiseData && apiLabWiseData.length > 0) {
                const matchedLab = apiLabWiseData.find(l => {
                    const lId = String(l?.labId ?? l?.lab_id ?? l?.id ?? '').toLowerCase();
                    const lName = String(l?.labName ?? l?.lab_name ?? l?.name ?? '').toLowerCase();
                    return (
                        (lId && (lId === selectedLabStr || lId === selectedLabIdStr)) ||
                        (lName && (lName === selectedLabStr || lName === selectedLabIdStr))
                    );
                });
                if (matchedLab && Array.isArray(matchedLab.items) && matchedLab.items.length > 0) {
                    items = matchedLab.items;
                }
            }

            if (items.length === 0) {
                // Fallback: filter from reviewsData if it contains learner-level items
                items = reviewsData.filter(item => {
                    const rName = String(item?.reviewName || item?.reviewTitle || item?.review_name || item?.name || '');
                    const lName = String(item?.labName || item?.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.labId || item?.lab_id || '');

                    const reviewMatch = !selectedReviewStr || selectedReviewStr === 'all' || rName.toLowerCase() === selectedReviewStr;
                    const labMatch = !selectedLabStr || selectedLabStr === 'all' || lName.toLowerCase() === selectedLabStr || (selectedLabIdStr && lName.toLowerCase() === selectedLabIdStr);

                    return reviewMatch && labMatch;
                });
            }
        }

        // Show only COMPLETED status data, filter out everything else (e.g. LC PENDING, REVIEW PENDING, etc.)
        return items.filter(item => {
            const status = String(item?.status || item?.reviewStatus || item?.review_status || '').trim().toLowerCase();
            return status === 'completed' || status === 'tlc_published';
        });
    }, [learnerSubmissions, reviewsData, selectedReview, selectedLab, selectedLabId, apiLabWiseData]);

    // Click handler for [View] on an individual review card
    const handleViewReviewCard = (rName, rId) => {
        let resolvedId = rId;
        if (!resolvedId || resolvedId === rName) {
            resolvedId = getReviewIdByName(rName) || rId;
        }
        setSelectedReview(rName);
        setSelectedReviewId(resolvedId);
        setSelectedLab(null);
        setSelectedLabId(null);
        setLearnerSubmissions([]);
        setLabSearchText('');
        setViewMode('lab_wise');
        setShowTable(true);
        fetchLabStats(resolvedId, rName);
    };

    // Click handler for [View] on a lab in lab-wise table
    const handleViewLabRow = (labIdentifier, labId) => {
        const labDisplay = String(labIdentifier || labId || '');
        setSelectedLab(labDisplay);
        setSelectedLabId(labId || labDisplay);
        setViewMode('learner_wise');
        setShowTable(true);
        fetchLearnerDataForLab(labId || labDisplay, labDisplay, selectedReview);
    };

    const selectStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        },
    };


    if (initialLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    width: '100%',
                    py: 8,
                    px: 2,
                    boxSizing: 'border-box'
                }}
            >
                <CircularProgress size={52} thickness={4} sx={{ color: '#1976d2', mb: 3 }} />
                <SchoolOutlinedIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#333', textAlign: 'center' }}>
                    Loading review metrics and dashboard counts...
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#757575', textAlign: 'center', mt: 0.5 }}>
                    Please wait while we fetch the latest data.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            className="learner-review-container"
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                boxSizing: 'border-box',
                pt: '4px'
            }}
        >
            {/* Breadcrumb Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, px: 0.5, flexWrap: 'wrap', gap: 1 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '14px', '& .MuiBreadcrumbs-separator': { mx: 1 } }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        sx={{ cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => {
                            setViewMode('reviews');
                            setSelectedReview(null);
                            setSelectedLab(null);
                            setReviewNameFilter('all');
                            setReviewNameInput(ALL_REVIEW_TITLES_LABEL);
                            setReviewSearchText('');
                        }}
                    >
                        Learner Reviews
                    </Link>

                    {viewMode === 'reviews' && (
                        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
                            Reviews Metrics
                        </Typography>
                    )}

                    {viewMode !== 'reviews' && (
                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => {
                                setViewMode('reviews');
                                setSelectedReview(null);
                                setSelectedLab(null);
                                setReviewNameFilter('all');
                                setReviewNameInput(ALL_REVIEW_TITLES_LABEL);
                                setReviewSearchText('');
                            }}
                        >
                            Reviews Metrics
                        </Link>
                    )}

                    {viewMode === 'lab_wise' && (
                        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
                            {selectedReview || 'All Reviews'} (Lab Wise Summary)
                        </Typography>
                    )}

                    {viewMode === 'learner_wise' && (
                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => {
                                setViewMode('lab_wise');
                                setSelectedLab(null);
                                setSelectedLabId(null);
                                setLearnerSubmissions([]);
                            }}
                        >
                            {selectedReview || 'All Reviews'}
                        </Link>
                    )}

                    {viewMode === 'learner_wise' && (
                        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
                            {selectedLab}
                        </Typography>
                    )}
                </Breadcrumbs>

                {viewMode !== 'reviews' && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            if (viewMode === 'learner_wise') {
                                setViewMode('lab_wise');
                                setSelectedLab(null);
                                setSelectedLabId(null);
                                setLearnerSubmissions([]);
                            } else if (viewMode === 'lab_wise') {
                                setViewMode('reviews');
                                setSelectedReview(null);
                                setSelectedReviewId(null);
                                setSelectedLab(null);
                                setSelectedLabId(null);
                                setLearnerSubmissions([]);
                                setReviewNameFilter('all');
                                setReviewNameInput(ALL_REVIEW_TITLES_LABEL);
                                fetchData(1, 50000, { reviewName: 'all' }, false, false);
                            }
                        }}
                        sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                    >
                        {viewMode === 'learner_wise' ? 'Back to Labs' : 'Back to Reviews'}
                    </Button>
                )}
            </Box>

            {/* Stat Cards Grid - 3 by 3 layout (3 cards per row, 2 rows) - Modern Clean White with Top Accent */}
            <Box
                className="mentor-review-stats-grid"
                sx={{
                    display: 'grid !important',
                    gridTemplateColumns: {
                        xs: 'repeat(1, 1fr) !important',
                        sm: 'repeat(2, 1fr) !important',
                        md: 'repeat(3, 1fr) !important',
                        lg: 'repeat(3, 1fr) !important',
                    },
                    gap: '14px',
                    mb: '16px',
                    pt: '4px',
                    px: '2px',
                    pb: '2px',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                }}
            >
                {/* 1. Total Learners - Blue Top Accent */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #3b82f6 !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Total Learners
                        <StatInfoIcon text="Displays the number of unique learners who have submitted at least one review out of the total eligible learners in the selected COE and semester." />
                    </span>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                            {formatStatValue(stats.totalLearnersGivenReview !== undefined ? stats.totalLearnersGivenReview : stats.totalLearners)}
                        </span>
                        {stats.totalLearnersGivenReview !== undefined && (
                            <Typography component="span" sx={{ fontSize: '16px', color: '#6c757d', fontWeight: 600, ml: 0.6 }}>
                                / {formatStatValue(stats.totalLearners)}
                            </Typography>
                        )}
                    </Box>
                    {stats.totalLearnersGivenReview !== undefined && (
                        <Typography sx={{ fontSize: '11px', color: '#6c757d', mt: 0.2, fontWeight: 500 }}>
                            Given / Total
                        </Typography>
                    )}
                </Box>

                {/* 2. Total Reviews - Indigo Top Accent (Not Clickable) */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #6366f1 !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Reviews Conducted
                        <StatInfoIcon text="Displays the total number of unique reviews that have been conducted, where at least one learner has submitted the review." />
                    </span>
                    <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatStatValue(stats.totalConductedReviews !== undefined ? stats.totalConductedReviews : stats.totalReviews)}
                    </span>
                </Box>

                {/* 3. Average Reviews - Cyan/Teal Top Accent */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #06b6d4 !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Average Reviews
                        <StatInfoIcon text="Displays the average number of review submissions received per conducted review." />
                    </span>
                    <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatStatValue(stats.averageReviews)}
                    </span>
                </Box>

                {/* 4. Average Score - Purple Top Accent */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #8b5cf6 !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Average Score
                        <StatInfoIcon text="Displays the average score across all conducted reviews that have received scores." />
                    </span>
                    <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatStatValue(stats.averageScore)}
                    </span>
                </Box>

                {/* 5. Max Score - Amber Top Accent */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #f59e0b !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Max Score
                        <StatInfoIcon text="Displays the highest individual score achieved by any learner across all conducted reviews." />
                    </span>
                    <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatStatValue(stats.maxScore)}
                    </span>
                </Box>

                {/* 6. Max Reviews - Rose Top Accent */}
                <Box
                    className="learner-review-stat-card"
                    sx={{
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e2e8f0 !important',
                        borderTop: '3px solid #f43f5e !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02) !important',
                        py: 1.4,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default !important',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.12) !important',
                        }
                    }}
                >
                    <span className="learner-review-stat-title" style={{ color: '#64748b', minHeight: '18px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px' }}>
                        Max Reviews
                        <StatInfoIcon text="Displays the highest number of submissions received by a single review." />
                    </span>
                    <span className="learner-review-stat-value" style={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatStatValue(stats.maxReviews)}
                    </span>
                </Box>
            </Box>

            {/* Filter Container: Left-aligned directly on top of table */}
            <Box
                className="learner-review-filters-container"
                sx={{
                    mb: '14px !important',
                    p: '10px 16px !important',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start !important',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Semester Filter */}
                <FormControl
                    size="small"
                    sx={{
                        width: { xs: '100%', sm: 190, md: 210 },
                        minWidth: { xs: '100%', sm: 170 },
                        flex: 'none'
                    }}
                >
                    <InputLabel id="semester-select-label" shrink>Semester</InputLabel>
                    <Select
                        labelId="semester-select-label"
                        id="semester-select"
                        name="semester"
                        value={semester || ''}
                        onChange={handleSemesterChange}
                        label="Semester"
                        notched
                        displayEmpty
                        renderValue={(selected) => (selected !== '' && selected !== null && selected !== undefined) ? selected : "All Semesters"}
                        sx={selectStyle}
                    >
                        <MenuItem value="">All Semesters</MenuItem>
                        {semesters.filter(sem => sem != null).map((sem) => (
                            <MenuItem key={sem} value={sem}>
                                {sem}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Review Name Filter */}
                <FormControl
                    size="small"
                    sx={{
                        width: { xs: '100%', sm: 260, md: 300 },
                        minWidth: { xs: '100%', sm: 220 },
                        flex: 'none'
                    }}
                >
                    <Autocomplete
                        size="small"
                        options={reviewNameOptions}
                        getOptionLabel={(option) => typeof option === 'string' ? option : (option?.label || "")}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            const optVal = typeof option === 'string' ? option : option?.value;
                            const valVal = typeof value === 'string' ? value : value?.value;
                            return optVal === valVal;
                        }}
                        filterOptions={(options, { inputValue }) => {
                            const q = (inputValue || '').trim().toLowerCase();
                            if (!q) return options;
                            return options.filter(opt => {
                                const label = (opt?.label || opt?.value || '').toLowerCase();
                                return label.includes(q);
                            });
                        }}
                        value={reviewNameInput === '' ? null : (reviewNameOptions.find(opt => opt.value === reviewNameFilter || opt.reviewName === reviewNameFilter) || (reviewNameFilter !== 'all' ? { label: reviewNameFilter, value: reviewNameFilter, reviewName: reviewNameFilter, reviewId: getReviewIdByName(reviewNameFilter) } : reviewNameOptions[0]) || null)}
                        inputValue={reviewNameInput}
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === 'input') {
                                setReviewNameInput(newInputValue);
                            } else if (reason === 'clear') {
                                setReviewNameInput('');
                            } else if (reason === 'reset') {
                                if (newInputValue && newInputValue !== ALL_REVIEW_TITLES_LABEL) {
                                    setReviewNameInput(newInputValue);
                                }
                            }
                        }}
                        onBlur={() => {
                            if (!reviewNameInput || !reviewNameInput.trim()) {
                                const current = reviewNameOptions.find(opt => opt.value === reviewNameFilter || opt.reviewName === reviewNameFilter);
                                setReviewNameInput(typeof current === 'string' ? current : (current?.label || (reviewNameFilter === 'all' ? ALL_REVIEW_TITLES_LABEL : reviewNameFilter)));
                            }
                        }}
                        onChange={(event, newValue, reason) => {
                            if (reason === 'clear' || !newValue) {
                                setReviewNameFilter('all');
                                setReviewNameInput('');
                                setSelectedReview(null);
                                setSelectedReviewId(null);
                                setSelectedLab(null);
                                setSelectedLabId(null);
                                setLearnerSubmissions([]);
                                setApiLabWiseData([]);
                                setViewMode('reviews');
                                setCurrentPage(1);
                                fetchData(1, 50000, { reviewName: 'all' }, false, true);
                                return;
                            }
                            const val = typeof newValue === 'string' ? newValue : (newValue.value || newValue.reviewName || 'all');
                            const label = typeof newValue === 'string' ? newValue : (newValue.label || val);
                            setReviewNameFilter(val);
                            setReviewNameInput(val === 'all' ? ALL_REVIEW_TITLES_LABEL : label);
                            setCurrentPage(1);

                            // A title spans multiple reviews, so always return to the reviews breakdown
                            setSelectedReview(null);
                            setSelectedReviewId(null);
                            setSelectedLab(null);
                            setSelectedLabId(null);
                            setLearnerSubmissions([]);
                            setApiLabWiseData([]);
                            setViewMode('reviews');
                            fetchData(1, 50000, { reviewName: val }, false, true);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Review Title"
                                InputLabelProps={{ shrink: true }}
                                placeholder="Search Review Title"
                                sx={selectStyle}
                            />
                        )}
                    />
                </FormControl>


                {/* Auto-Refresh Status Indicator & Refresh Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        ml: { md: 'auto', xs: 0 },
                        py: 0.5,
                        px: 0.5,
                        flexWrap: 'nowrap'
                    }}
                >
                    {/* Green Live Dot */}
                    <Box
                        sx={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            display: 'inline-block',
                            boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.25)',
                            flexShrink: 0
                        }}
                    />

                    {/* Auto-refresh text with Last updated timestamp */}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '13px',
                            color: '#555',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        Auto-refreshes every 1 min{' '}
                        <Box component="span" sx={{ color: '#777', fontWeight: 400 }}>
                            (Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) : ''})
                        </Box>
                    </Typography>

                    {/* Refresh Icon */}
                    <Tooltip title="Refresh now" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleManualRefresh}
                                disabled={isRefreshing || actionLoading}
                                sx={{
                                    p: 0.5,
                                    color: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <RefreshIcon
                                    sx={{
                                        fontSize: 20,
                                        color: '#1976d2',
                                        animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' },
                                        },
                                    }}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>



            {/* Action Loading Indicator */}
            {actionLoading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '300px', width: '100%', mt: 4 }}>
                    <CircularProgress size={50} />
                </Box>
            )}

            {/* VIEW MODE 2: Individual Review Cards (Review 1, Review 2, Review 3...) */}
            {!actionLoading && viewMode === 'reviews' && (
                <Box sx={{ width: '100%', mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>
                            {/* Individual Reviews ({filteredReviewsSummaryList.length}) */}
                            Reviews Breakdown
                        </Typography>

                        {/* Search Reviews Input */}
                        <TextField
                            size="small"
                            placeholder="Search by review name or date..."
                            value={reviewSearchText}
                            onChange={(e) => setReviewSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon sx={{ color: '#757575', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: reviewSearchText ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setReviewSearchText('')}
                                            edge="end"
                                            aria-label="clear search"
                                        >
                                            <ClearIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{ minWidth: { xs: '100%', sm: 300 }, backgroundColor: '#fff', borderRadius: '8px' }}
                        />
                    </Box>

                    {filteredReviewsSummaryList.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            <Typography sx={{ color: '#6c757d', fontSize: '15px' }}>
                                {reviewSearchText ? `No reviews found matching "${reviewSearchText}"` : 'No review records found for the selected semester.'}
                            </Typography>
                        </Box>
                    ) : (
                        paginatedReviewCards.map((rev, idx) => (
                            <Box
                                key={`${rev.reviewId}-${rev.reviewName}-${rev.reviewDate || rev.latestDate}-${idx}`}
                                sx={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '10px',
                                    p: 2.5,
                                    mb: 2,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        borderColor: '#1976d2',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <AssignmentOutlinedIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                                        <Box>
                                            {rev.reviewTitle && (
                                                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#212529' }}>
                                                    {highlightMatch(rev.reviewTitle, reviewSearchText)}
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontSize: '13px', color: '#6c757d', fontWeight: 500, mt: 0.2 }}>
                                                {highlightMatch(rev.reviewName, reviewSearchText)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleViewReviewCard(rev.reviewName, rev.reviewId)}
                                        endIcon={<VisibilityOutlinedIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            px: 3,
                                            py: 0.8
                                        }}
                                    >
                                        VIEW
                                    </Button>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, 1fr)',
                                            sm: 'repeat(3, 1fr)',
                                            md: 'repeat(6, 1fr)'
                                        },
                                        gap: 2,
                                        backgroundColor: '#f8f9fa',
                                        p: 1.8,
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Total Learners
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#212529', mt: 0.3 }}>
                                            {formatStatValue(rev.totalLearners)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Total Reviews Taken
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#212529', mt: 0.3 }}>
                                            {formatStatValue(rev.totalReviews)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', mt: 0.2 }}>
                                            Date: {highlightMatch(rev.latestDate, reviewSearchText)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Avg Score
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#2e7d32', mt: 0.3 }}>
                                            {formatStatValue(rev.avgScore)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Max Score
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1976d2', mt: 0.3 }}>
                                            {formatStatValue(rev.maxScore)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Completed
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#2e7d32', mt: 0.3 }}>
                                            {formatStatValue(rev.completed)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>
                                            In Prog.
                                        </Typography>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#ed6c02', mt: 0.3 }}>
                                            {formatStatValue(rev.inProgress)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}

                    {filteredReviewsSummaryList.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: { xs: 'center', md: 'space-between' },
                                alignItems: 'center',
                                mt: 3,
                                mb: 2,
                                px: 1,
                                gap: 2,
                                flexWrap: 'wrap'
                            }}
                        >
                            {/* Left: Rows Per Page Selector & Summary Count */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px', color: '#6c757d', fontWeight: 500 }}>
                                        Rows per page:
                                    </Typography>
                                    <FormControl size="small">
                                        <Select
                                            value={cardsPerPage}
                                            onChange={(e) => {
                                                const newSize = parseInt(e.target.value, 10);
                                                setCardsPerPage(newSize);
                                                setCardsPage(1);
                                                setReportViewerReviewState(prev => ({
                                                    ...prev,
                                                    cardsPerPage: newSize,
                                                    cardsPage: 1
                                                }));
                                            }}
                                            sx={{
                                                fontSize: '13px',
                                                height: '32px',
                                                backgroundColor: '#ffffff',
                                                borderRadius: '6px',
                                                '& .MuiSelect-select': { py: '4px', px: '10px', fontWeight: 600, color: '#212529' },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' }
                                            }}
                                        >
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={20}>20</MenuItem>
                                            <MenuItem value={30}>30</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                            <MenuItem value={100}>100</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Typography sx={{ fontSize: '13px', color: '#6c757d' }}>
                                    Showing {filteredReviewsSummaryList.length === 0 ? 0 : ((cardsPage - 1) * cardsPerPage) + 1}–{Math.min(cardsPage * cardsPerPage, filteredReviewsSummaryList.length)} of {filteredReviewsSummaryList.length} reviews
                                </Typography>
                            </Box>

                            {/* Right: Page Navigation */}
                            {totalCardPages > 1 && (
                                <Pagination
                                    count={totalCardPages}
                                    page={cardsPage}
                                    onChange={(e, val) => {
                                        setCardsPage(val);
                                        setReportViewerReviewState(prev => ({ ...prev, cardsPage: val }));
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    color="primary"
                                    shape="rounded"
                                    showFirstButton
                                    showLastButton
                                />
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* VIEW MODE 3: Lab-Wise Table for Selected Review */}
            {!actionLoading && viewMode === 'lab_wise' && (
                <Box sx={{ width: '100%', mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#212529' }}>
                                Lab Wise Data: {selectedReview}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: '#6c757d' }}>
                                Showing lab-wise performance for {selectedReview}
                            </Typography>
                        </Box>

                        {/* Search by Lab Input */}
                        <TextField
                            size="small"
                            placeholder="Search by Lab..."
                            value={labSearchText}
                            onChange={(e) => setLabSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon sx={{ color: '#757575', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: { xs: '100%', sm: 260 }, backgroundColor: '#fff', borderRadius: '8px' }}
                        />
                    </Box>

                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                        <Table aria-label="lab-wise table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>LAB NAME</StyledTableCell>
                                    <StyledTableCell align="center">LEARNERS</StyledTableCell>
                                    <StyledTableCell align="center">REVIEWS</StyledTableCell>
                                    <StyledTableCell align="center">AVG SCORE</StyledTableCell>
                                    <StyledTableCell align="center">MAX SCORE</StyledTableCell>
                                    <StyledTableCell align="center">COMPLETED</StyledTableCell>
                                    <StyledTableCell align="center">IN PROG</StyledTableCell>
                                    <StyledTableCell align="right">ACTION</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {labStatsLoading ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={30} />
                                        </StyledTableCell>
                                    </TableRow>
                                ) : labWiseData.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography sx={{ color: '#6c757d' }}>
                                                {labSearchText ? `No labs found matching "${labSearchText}"` : 'No lab data available for this review.'}
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    labWiseData.map((lab) => (
                                        <StyledTableRow key={lab.labId}>
                                            <StyledTableCell sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                {lab.labName || lab.labId}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {formatStatValue(lab.totalLearners)}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {formatStatValue(lab.totalReviews)}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                                {formatStatValue(lab.avgScore)}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                {formatStatValue(lab.maxScore)}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                                {formatStatValue(lab.completed)}
                                            </StyledTableCell>
                                            <StyledTableCell align="center" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                                                {formatStatValue(lab.inProgress)}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {(() => {
                                                    const isCompletedZero = (Number(lab.completed) || 0) === 0;
                                                    const viewBtn = (
                                                        <span style={{ display: 'inline-block', cursor: isCompletedZero ? 'not-allowed' : 'default' }}>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                color="primary"
                                                                disabled={isCompletedZero}
                                                                onClick={() => handleViewLabRow(lab.labName || lab.labId, lab.labId)}
                                                                endIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '12px',
                                                                    borderRadius: '6px',
                                                                    px: 2,
                                                                    ...(isCompletedZero && {
                                                                        pointerEvents: 'none'
                                                                    })
                                                                }}
                                                            >
                                                                VIEW
                                                            </Button>
                                                        </span>
                                                    );

                                                    return isCompletedZero ? (
                                                        <Tooltip title="Evaluation results will be available once mentor verification is completed." arrow placement="top">
                                                            {viewBtn}
                                                        </Tooltip>
                                                    ) : (
                                                        viewBtn
                                                    );
                                                })()}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* VIEW MODE 4: Learner-Wise Table (Detailed Current Table) */}
            {!actionLoading && viewMode === 'learner_wise' && (
                <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, display: 'block', mt: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>
                            Learner Submissions: {selectedReview} — {selectedLab}
                        </Typography>

                    </Box>

                    <MentorReviewTable
                        data={learnerWiseData}
                        loading={actionLoading || learnerLoading}
                        totalCount={learnerWiseData.length}
                        currentPage={currentPage}
                        selectedReviewName={selectedReview || reviewNameFilter}
                        onPageChange={(page) => {
                            setCurrentPage(page);
                        }}
                        onRowsPerPageChange={(page, limit) => {
                            setCurrentPage(1);
                            setRowsPerPage(limit);
                        }}
                        onRefresh={() => {
                            fetchLearnerDataForLab(selectedLabId || selectedLab, selectedLab, selectedReview);
                        }}
                        isAdmin={true}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ReportViewerReview;
